
import { ApiKey, CreateApiKeyData } from "@/models/api-key";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

export const ApiKeyService = {
  async getApiKeys(): Promise<ApiKey[]> {
    try {
      // Check if user is logged in
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error("User is not authenticated");
      }

      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("ai_llm_keys")
        .single();

      if (profileError) {
        console.error("Failed to fetch API keys:", profileError);
        return [];
      }

      const apiKeys: ApiKey[] = [];
      if (profile?.ai_llm_keys) {
        // Convert stored API keys to the ApiKey format
        const keysObj = profile.ai_llm_keys as Record<string, string>;
        
        Object.entries(keysObj).forEach(([provider, key]) => {
          if (key) {
            apiKeys.push({
              id: provider,
              provider: provider.charAt(0).toUpperCase() + provider.slice(1), // Capitalize first letter
              maskedKey: `${provider.substring(0, 2).toLowerCase()}_•••••••••••••••••`,
              userId: sessionData.session.user.id,
              createdAt: new Date()
            });
          }
        });
      }
      
      return apiKeys;
    } catch (error) {
      console.error("Failed to fetch API keys:", error);
      toast({
        title: "Error",
        description: "Failed to load API keys. Please try again.",
        variant: "destructive",
      });
      return [];
    }
  },

  async createApiKey(data: CreateApiKeyData): Promise<ApiKey | null> {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error("User is not authenticated");
      }
      
      // Call the Edge Function to update the API key
      const { error } = await supabase.functions.invoke("update-api-keys", {
        body: {
          provider: data.provider.toLowerCase(),
          apiKey: data.key
        }
      });

      if (error) {
        throw error;
      }
      
      // Create masked version of the key
      const maskedKey = `${data.provider.substring(0, 2).toLowerCase()}_•••••••••••••••••`;
      
      // Return the API key object
      const apiKey: ApiKey = {
        id: data.provider.toLowerCase(),
        provider: data.provider,
        maskedKey,
        userId: sessionData.session.user.id,
        createdAt: new Date()
      };
      
      toast({
        title: "Success",
        description: "API key added successfully!",
      });
      
      return apiKey;
    } catch (error) {
      console.error("Failed to add API key:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add API key. Please try again.",
        variant: "destructive",
      });
      return null;
    }
  },

  async deleteApiKey(id: string): Promise<boolean> {
    try {
      // We need to update the user's profile to remove the specific API key
      // First, get the existing keys
      const { data: profile, error: getError } = await supabase
        .from("profiles")
        .select("ai_llm_keys, id")
        .single();

      if (getError) throw getError;

      // Create a new keys object without the key to be deleted
      const updatedKeys = { ...(profile?.ai_llm_keys as Record<string, string> || {}) };
      delete updatedKeys[id];

      // Update the profile with the new keys object
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ ai_llm_keys: updatedKeys })
        .eq("id", profile.id);

      if (updateError) throw updateError;
      
      toast({
        title: "Success",
        description: "API key removed successfully!",
      });
      
      return true;
    } catch (error) {
      console.error(`Failed to delete API key with ID ${id}:`, error);
      toast({
        title: "Error",
        description: "Failed to remove API key. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  }
};
